import createDeepEqualSelector from 'utils/reselect/typescript/createDeepEqualSelector';
import { selectEquipmentSetsArray } from 'services/equipmentSets/selectors';
import { AppState } from 'services/types';
import { SprayMethod, SprayApplicationRate } from 'services/sprayEvents/types';
import { EquipmentSet } from 'services/equipmentSets/types';
import { selectEntity } from 'services/entities/selectors';
import { NozzleType } from 'services/equipmentSets/constants';
import { OwnProps } from './index';

const selectSprayMethod = (
    state: AppState,
    props: OwnProps
): SprayMethod | undefined => props.sprayMethod;

const selectApplicationRate = (
    state: AppState,
    props: OwnProps
): SprayApplicationRate | undefined => props.applicationRate;

const selectLabelDescription = createDeepEqualSelector(
    selectSprayMethod,
    selectApplicationRate,
    (sprayMethod, applicationRate) => {
        const sprayMethodDescription =
            NozzleType.WEED_BOOM === sprayMethod ? 'Weed Boom' : sprayMethod;

        return sprayMethod && applicationRate
            ? `(with ${applicationRate}L/ha calibrations and ${sprayMethodDescription} nozzles)`
            : '';
    }
);

const makeSelectLabelDescription = (): typeof selectLabelDescription =>
    selectLabelDescription;

const selectFilteredEquipmentSets = createDeepEqualSelector(
    selectEquipmentSetsArray,
    selectEntity,
    (equipmentSets, entity) => {
        // If entity id is not provided, then it does not filter by sector
        if (!entity) {
            return equipmentSets;
        }

        return equipmentSets.filter((equipmentSet) => {
            const [equipmentSetEntity] = equipmentSet.entities;

            if (!equipmentSetEntity) {
                return true;
            }

            const fullPath =
                entity.path == null
                    ? `/${entity.id}`
                    : `${entity.path}/${entity.id}`;

            return (
                fullPath === `/${equipmentSetEntity.id}` ||
                fullPath.includes(`/${equipmentSetEntity.id}/`)
            );
        });
    }
);

const selectOptions = createDeepEqualSelector(
    selectFilteredEquipmentSets,
    selectSprayMethod,
    selectApplicationRate,
    (equipmentSetsArray, sprayMethod, applicationRate) => {
        const result: {
            key: number;
            text: string;
            value: number;
        }[] = [];

        if (equipmentSetsArray) {
            equipmentSetsArray.forEach((item: EquipmentSet) => {
                const { id, name, nozzleType, calibrations = [] } = item;

                if (sprayMethod && sprayMethod !== nozzleType) return;

                let hasCalibrationsWithApplicationRate = false;
                for (const calibration of calibrations) {
                    if (calibration.applicationRate === applicationRate) {
                        hasCalibrationsWithApplicationRate = true;
                        break;
                    }
                }
                if (!hasCalibrationsWithApplicationRate) return;

                result.push({
                    key: id,
                    text: name,
                    value: id
                });
            });
        }

        return result;
    }
);

const makeSelectOptions = (): typeof selectOptions => selectOptions;

export {
    makeSelectOptions,
    selectFilteredEquipmentSets,
    makeSelectLabelDescription
};
